import React, { useState, useEffect } from 'react';
import './actionModals.css';

// ---- Date format helpers (DD-MM-YYYY ↔ YYYY-MM-DD for <input type="date">) ----
const toInputDate = (str) => {
  if (!str) return '';
  // DD-MM-YYYY → YYYY-MM-DD
  const m1 = /^(\d{2})-(\d{2})-(\d{4})$/.exec(str);
  if (m1) return `${m1[3]}-${m1[2]}-${m1[1]}`;
  // Already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  // ISO datetime (e.g. 2024-07-02T00:00:00.000Z) — take date part
  const iso = /^(\d{4}-\d{2}-\d{2})T/.exec(str);
  if (iso) return iso[1];
  // Try parsing any other format via Date
  const d = new Date(str);
  if (!isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
  }
  return '';
};

const fromInputDate = (str) => {
  if (!str) return '';
  // YYYY-MM-DD → DD-MM-YYYY
  const parts = str.split('-');
  if (parts.length === 3 && parts[0].length === 4) return `${parts[2]}-${parts[1]}-${parts[0]}`;
  return str;
};

// ============================================================
// Export — paginated GET until all rows loaded (progress per backend response)
// ============================================================
export async function fetchAllRowsPaged({
  fetchPage,
  pageSize = 5000,
  extraQuery = {},
  onProgress,
}) {
  const first = await fetchPage({ page: 1, limit: pageSize, ...extraQuery });
  if (!first || first.status?.code !== 200) {
    const err = new Error(first?.error?.message || 'Failed to load data');
    err.apiResponse = first;
    throw err;
  }

  const total = Number(first.data?.pagination?.total ?? 0);
  const apiPages = first.data?.pagination?.totalPages;
  const totalPages =
    apiPages != null && Number(apiPages) > 0
      ? Number(apiPages)
      : Math.max(1, Math.ceil(total / pageSize));

  let rows = [...(first.data?.records ?? [])];
  onProgress?.({ loaded: rows.length, total, page: 1, totalPages });

  for (let p = 2; p <= totalPages; p += 1) {
    const res = await fetchPage({ page: p, limit: pageSize, ...extraQuery });
    if (!res || res.status?.code !== 200) {
      throw new Error(res?.error?.message || `Failed to load page ${p}`);
    }
    rows = rows.concat(res.data?.records ?? []);
    onProgress?.({ loaded: rows.length, total, page: p, totalPages });
  }

  return { rows, total };
}

// ============================================================
// DELETE MODAL — requires admin password confirmation
// ============================================================
export function DeleteModal({
  title = 'Delete Record',
  onConfirm,
  onClose,
  loading,
  description,
  confirmButtonText = 'Delete',
}) {
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd]   = useState(false);

  useEffect(() => { setPassword(''); setShowPwd(false); }, [title]);

  const defaultDescription = (
    <>
      This action is <strong>permanent</strong> and cannot be undone.<br />
      Enter your admin password to confirm.
    </>
  );

  return (
    <div className="am-overlay" onClick={onClose}>
      <div className="am-modal am-modal-sm" onClick={e => e.stopPropagation()}>
        <div className="am-header am-header-danger">
          <span className="am-header-icon">🗑</span>
          <h2>{title}</h2>
          <button className="am-close" onClick={onClose}>&times;</button>
        </div>
        <div className="am-body">
          <p className="am-delete-msg">
            {description != null ? description : defaultDescription}
          </p>
          <div className="am-pwd-wrap">
            <input
              type={showPwd ? 'text' : 'password'}
              className="am-pwd-input"
              placeholder="Admin password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && password && onConfirm(password)}
              autoFocus
            />
            <button type="button" className="am-pwd-toggle" onClick={() => setShowPwd(p => !p)}>
              {showPwd ? '🙈' : '👁'}
            </button>
          </div>
        </div>
        <div className="am-footer">
          <button className="am-btn am-btn-cancel" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="am-btn am-btn-danger" onClick={() => onConfirm(password)} disabled={!password || loading}>
            {loading ? 'Deleting…' : confirmButtonText}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// EDIT MODAL — generic field-based form
// Supports field types: 'text' (default), 'textarea', 'date', 'number'
// For type='date': stores & receives DD-MM-YYYY, renders as date picker
// For type='computed': read-only, value auto-derived by onCompute callback
// ============================================================
export function EditModal({ title = 'Edit Record', fields, initialData, onSave, onClose, loading }) {
  const [form, setForm] = useState({});

  useEffect(() => {
    const init = {};
    fields.forEach(f => {
      const raw = initialData?.[f.key] ?? '';
      // For date fields: keep stored DD-MM-YYYY in form state, picker shows converted
      init[f.key] = raw != null ? String(raw) : '';
    });
    setForm(init);
  }, [initialData]);

  const handleChange = (key, rawVal, fieldDef) => {
    // For date inputs, convert YYYY-MM-DD back to DD-MM-YYYY for storage
    const storedVal = fieldDef?.type === 'date' ? fromInputDate(rawVal) : rawVal;
    setForm(prev => {
      const updated = { ...prev, [key]: storedVal };
      // If a date field has an onCompute dependency, recalculate computed fields
      return updated;
    });
  };

  // Build the data to save — send stored values (DD-MM-YYYY for dates)
  const handleSave = () => {
    const out = { ...form };
    onSave(out);
  };

  return (
    <div className="am-overlay" onClick={onClose}>
      <div className="am-modal am-modal-lg" onClick={e => e.stopPropagation()}>
        <div className="am-header">
          <span className="am-header-icon">✏️</span>
          <h2>{title}</h2>
          <button className="am-close" onClick={onClose}>&times;</button>
        </div>

        <div className="am-body am-body-scroll">
          <div className="am-form-grid">
            {fields.map(f => (
              <div key={f.key} className={`am-field ${f.fullWidth ? 'am-field-full' : ''}`}>
                <label className="am-label">{f.label}</label>

                {f.type === 'textarea' ? (
                  <textarea
                    className="am-input"
                    rows={2}
                    value={form[f.key] ?? ''}
                    onChange={e => handleChange(f.key, e.target.value, f)}
                    readOnly={f.readOnly}
                  />
                ) : f.type === 'date' ? (
                  <input
                    type="date"
                    className="am-input am-input-date"
                    value={toInputDate(form[f.key])}
                    onChange={e => handleChange(f.key, e.target.value, f)}
                    readOnly={f.readOnly}
                  />
                ) : (
                  <input
                    type={f.type || 'text'}
                    className={`am-input ${f.readOnly ? 'am-input-readonly' : ''}`}
                    value={form[f.key] ?? ''}
                    onChange={e => handleChange(f.key, e.target.value, f)}
                    readOnly={f.readOnly}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="am-footer">
          <button className="am-btn am-btn-cancel" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="am-btn am-btn-save" onClick={handleSave} disabled={loading}>
            {loading ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
